import React from "react";
import Box from "@mui/joy/Box";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Game } from "../../../models/games";
import {
    fromDatetimeLocalValue,
    toDatetimeLocalValue,
} from "../../bids/analytics/format";

export type UeFiltersState = {
    from: Date;
    to: Date;
    gameId: number | undefined;
    take: number;
};

export type UeFiltersProps = {
    value: UeFiltersState;
    onChange: (next: UeFiltersState) => void;
    onRefresh: () => void;
    loading?: boolean;
    games?: Game[];
    gamesLoading?: boolean;
    showGame?: boolean;
    showTake?: boolean;
};

export function UeFilters(props: UeFiltersProps) {
    const {
        value,
        onChange,
        onRefresh,
        loading,
        games = [],
        gamesLoading,
        showGame = false,
        showTake = false,
    } = props;

    return (
        <Box
            sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "flex-end",
                p: { xs: 1.5, sm: 2 },
                bgcolor: "background.level1",
                borderRadius: "md",
            }}
        >
            <FormControl size="sm">
                <FormLabel>С</FormLabel>
                <Input
                    type="datetime-local"
                    value={toDatetimeLocalValue(value.from)}
                    onChange={(e) =>
                        onChange({ ...value, from: fromDatetimeLocalValue(e.target.value) })
                    }
                />
            </FormControl>
            <FormControl size="sm">
                <FormLabel>По</FormLabel>
                <Input
                    type="datetime-local"
                    value={toDatetimeLocalValue(value.to)}
                    onChange={(e) =>
                        onChange({ ...value, to: fromDatetimeLocalValue(e.target.value) })
                    }
                />
            </FormControl>
            {showGame && (
                <FormControl size="sm" sx={{ minWidth: 160 }}>
                    <FormLabel>Игра</FormLabel>
                    <Select
                        size="sm"
                        placeholder="Все игры"
                        value={value.gameId ?? null}
                        disabled={gamesLoading}
                        startDecorator={<VideogameAssetIcon />}
                        onChange={(_, v) =>
                            onChange({
                                ...value,
                                gameId: typeof v === "number" ? v : undefined,
                            })
                        }
                    >
                        <Option value={null}>Все игры</Option>
                        {games.map((g) => (
                            <Option key={g.id} value={g.id}>
                                {g.name || g.alias || `Игра ${g.id}`}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
            )}
            {showTake && (
                <FormControl size="sm" sx={{ width: 100 }}>
                    <FormLabel>Топ</FormLabel>
                    <Input
                        type="number"
                        value={value.take}
                        onChange={(e) =>
                            onChange({
                                ...value,
                                take: Math.max(1, Math.min(100, Number(e.target.value) || 20)),
                            })
                        }
                    />
                </FormControl>
            )}
            <Button
                size="sm"
                variant="soft"
                startDecorator={loading ? <CircularProgress size="sm" /> : <RefreshIcon />}
                onClick={onRefresh}
                disabled={loading}
            >
                Обновить
            </Button>
        </Box>
    );
}

export function useUeFilters(): [UeFiltersState, React.Dispatch<React.SetStateAction<UeFiltersState>>] {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    from.setHours(0, 0, 0, 0);
    return React.useState<UeFiltersState>({
        from,
        to,
        gameId: undefined,
        take: 20,
    });
}
